import { SearchPageChrome } from "@/components/search/page-chrome"
import { PlusCircle, Search, Trash } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ShortlistList } from "@/components/search/shortlists/shortlist-list"
import { ActionLink } from "@/components/shared/action-buttons"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Card, CardContent } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import type SearchShortlistsShortlistList from "@/types/serializers/SearchShortlistsShortlistList"

import type { IndexProps } from "./page-props"
import { Link, useProps } from "@/lib/page-rendering"

export default function Page() {
  const { shortlists, capabilities } = useProps<IndexProps>()
  return (
    <Layout canCreate={capabilities.can_create}>
      <div className="flex h-full w-full flex-col gap-5">
        <ShortlistList
          ActionsComponent={Actions}
          shortlists={shortlists}
          canCreate={capabilities.can_create}
        />
      </div>
    </Layout>
  )
}

function Actions({ shortlist }: { shortlist: SearchShortlistsShortlistList }) {
  const { t } = useTranslation(["shortlists", "common"])

  return (
    <>
      <Button asChild>
        <Link href={`/search/shortlists/${shortlist.id}`}>
          <Search />
          {t("shortlists:index.view")}
        </Link>
      </Button>
      <ConfirmationDialog
        action={
          <Link
            className={buttonVariants({ variant: "destructive" })}
            href={`/search/shortlists/${shortlist.id}`}
            method="delete"
            as={Button}
          >
            {t("common:actions.confirm")}
          </Link>
        }
      >
        <ConfirmationDialogTrigger asChild>
          <Button variant={"destructive-outline"}>
            <Trash />
            {t("common:actions.delete")}
          </Button>
        </ConfirmationDialogTrigger>
      </ConfirmationDialog>
    </>
  )
}

function Layout({
  canCreate,
  children,
}: {
  canCreate: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation("shortlists")
  return (
    <>
      <SearchPageChrome
        action={
          canCreate ? (
            <ActionLink
              href="/search/shortlists/new"
              Icon={PlusCircle}
              variant="solid"
            >
              {t("index.create")}
            </ActionLink>
          ) : undefined
        }
      />
      <PageBody>
        <Card>
          <CardContent className="flex flex-col gap-8 px-4 py-4">
            {children}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
